import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, finalize, take } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UserService } from 'src/app/services/user.service';
import { UploadFileService } from 'src/app/services/upload-file.service';
import { ModalManagerService } from 'src/app/services/modal-manager.service';
import { ResourceItemModel, ResourceItem } from 'src/app/models/resource';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { ThemeService } from 'src/app/services/theme.service';
import { User, InstitutionTheme } from '../../../models/user';
import { PanelData } from '../../settings.constants';
import { VirtualBackgroundUtilsService } from '../../../services/virtual-background/virtual-background-utils.service';
import { ALLOWED_IMAGE_TYPES } from '../../../services/virtual-background/virtual-background-panel-base';
import { SettingsNotificationService } from '../../../settings/shared/services/settings-notifications.service';
import {
  IntegrationsFormService,
  FormState,
  FormAction,
} from '../user-settings-integrations/services/integrations-form.service';

export enum LogoType {
  HORIZONTAL_LOGO = 'horizontalLogo',
  SQUARE_LOGO = 'squareLogo',
  WATERMARK_LOGO = 'waterMarkLogo',
}

export interface BrandingFieldConfig {
  key: string;
  cssProperty: string;
  title: string;
  supportsGradients?: boolean;
  errorMessage?: string;
}

export interface BrandingSectionConfig {
  key: string;
  title: string;
  fields: BrandingFieldConfig[];
}

export interface BrandingAssetConfig {
  key: string;
  type: LogoType;
  title: string;
  description: string;
}

@UntilDestroy()
@Component({
  selector: 'app-user-settings-branding',
  templateUrl: './user-settings-branding.component.html',
  styleUrls: ['./user-settings-branding.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsBrandingComponent implements OnInit {
  @Input() panelData?: PanelData;
  @Input() form!: UntypedFormGroup;
  @Input() isMobile = false;
  @Output() formChangedEvent: EventEmitter<boolean> = new EventEmitter();
  @Output() goBack = new EventEmitter<void>();

  user?: User;

  // Make LogoType and configuration accessible in template
  readonly LogoType = LogoType;
  readonly IconTypes = IconTypes;
  brandingConfig: {
    sections: BrandingSectionConfig[];
    assets: BrandingAssetConfig[];
    defaultErrorMessage: string;
  } = { sections: [], assets: [], defaultErrorMessage: '' };

  public initialFormValue: unknown;
  protected formState$ = this.integrationsFormService.getFormState$();
  protected FormState = FormState;

  // Virtual Backgrounds
  virtualBackgrounds: ResourceItem[] = [];
  isLoadingVirtualBackgrounds$ = new BehaviorSubject<boolean>(false);
  readonly ALLOWED_IMAGE_TYPES = ALLOWED_IMAGE_TYPES;
  private defaultTheme: InstitutionTheme | null = null;

  constructor(
    private userService: UserService,
    private settingsNotificationService: SettingsNotificationService,
    private translateService: TranslateService,
    private formBuilder: UntypedFormBuilder,
    private uploadService: UploadFileService,
    private integrationsFormService: IntegrationsFormService,
    private modalManagerService: ModalManagerService,
    private vbUtilsService: VirtualBackgroundUtilsService,
    private themeService: ThemeService,
  ) {}

  ngOnInit(): void {
    // Initialize the branding configuration with translations
    this.brandingConfig = this.getBrandingConfig();

    // Get current user value without subscription since user won't change while on this component
    this.user = this.userService.user.getValue()?.user;
    this.defaultTheme = this.themeService.getDefaultTheme();
    this.initForm();

    // Set up form state subscription to emit changes to parent
    this.formState$.pipe(untilDestroyed(this)).subscribe((state) => {
      this.formChangedEvent.emit(state !== FormState.Initial);
    });

    // Set up form action subscription to handle submit/cancel actions
    this.integrationsFormService
      .getFormAction$()
      .pipe(untilDestroyed(this))
      .subscribe((action) => {
        if (action === FormAction.SUBMIT) {
          this.performSubmit();
        } else if (action === FormAction.CANCEL) {
          // Form service resets form to initial value, reload backgrounds
          this.syncVirtualBackgroundsToForm();
        }
      });
  }

  private getBrandingConfig(): {
    sections: BrandingSectionConfig[];
    assets: BrandingAssetConfig[];
    defaultErrorMessage: string;
  } {
    return {
      sections: [
        {
          key: 'insideSpaces',
          title: this.translateService.instant('Inside Spaces'),
          fields: [
            {
              key: 'topBarBackground',
              cssProperty: '--top-bar-background',
              title: this.translateService.instant('Top Bar background color'),
            },
            {
              key: 'galleryBackground',
              cssProperty: '--gallery-background',
              title: this.translateService.instant('Gallery View background color'),

              supportsGradients: true,
              errorMessage: this.translateService.instant(
                'Enter a valid hex color or linear gradient',
              ),
            },
            {
              key: 'galleryBottomBarBackground',
              cssProperty: '--gallery-bottom-bar-background',
              title: this.translateService.instant('Gallery View Bottom Bar background color'),
            },
            {
              key: 'greyContainerBackground',
              cssProperty: '--grey-container-background',
              title: this.translateService.instant('Waiting Room background color'),
            },
          ],
        },
        {
          key: 'outsideSpaces',
          title: this.translateService.instant('Outside Spaces'),
          fields: [
            {
              key: 'sidebarBG',
              cssProperty: '--sidebar-background',
              title: this.translateService.instant('Spaces Manager Sidebar background color'),
            },
            {
              key: 'switcherBG',
              cssProperty: '--switcher-background',
              title: this.translateService.instant(
                'Spaces Manager Switcher Sidebar background color',
              ),
            },
            {
              key: 'sidebarActiveBG',
              cssProperty: '--sidebar-active-background',
              title: this.translateService.instant(
                'Spaces Manager Sidebar Active Item background color',
              ),
            },
            {
              key: 'primaryBrandColor',
              cssProperty: '--primary-brand-color',
              title: this.translateService.instant('Primary Brand color'),
            },
          ],
        },
      ],
      assets: [
        {
          key: 'horizontalLogo',
          type: LogoType.HORIZONTAL_LOGO,
          title: this.translateService.instant('Horizontal Image'),
          description: this.translateService.instant('Appears in the Waiting Room and on emails'),
        },
        {
          key: 'waterMarkLogo',
          type: LogoType.WATERMARK_LOGO,
          title: this.translateService.instant('Watermark Image'),
          description: this.translateService.instant(
            'Appears on the bottom left of the Whiteboard. For best results, use a PNG with a transparent background. Max size: 128 × 64 px.',
          ),
        },
        {
          key: 'squareLogo',
          type: LogoType.SQUARE_LOGO,
          title: this.translateService.instant('Square Image'),
          description: this.translateService.instant(
            'Appears on the video call tiles of teachers and admins in your institution',
          ),
        },
      ],
      defaultErrorMessage: this.translateService.instant('Enter a valid hex color'),
    };
  }

  async initForm(): Promise<void> {
    // Initialize the settings form group if it doesn't exist
    if (!this.form.get('settings')) {
      this.form.addControl('settings', this.formBuilder.group({}));
    }

    const settingsGroup = this.form.get('settings') as UntypedFormGroup;
    const institutionTheme = this.user?.institution?.theme;
    const defaultBrandingSettings: Record<string, string | undefined> = {};

    const theme = this.themeService.getThemeWithDefaults(institutionTheme);

    // Add assets from configuration
    this.brandingConfig.assets.forEach((asset) => {
      defaultBrandingSettings[asset.key] = this.getAssetValue(asset.key, institutionTheme);
    });

    // Add colors from configuration
    this.brandingConfig.sections.forEach((section) => {
      section.fields.forEach((field) => {
        defaultBrandingSettings[field.key] = theme.colors[field.cssProperty];
      });
    });

    this.patchFormWithValues(defaultBrandingSettings);

    // Initialize onlyAllowInstitutionVirtualBackgrounds from existing theme.virtualBackgroundSettings.isRestricted
    const isRestricted = institutionTheme?.virtualBackgroundSettings?.isRestricted || false;
    settingsGroup.get('onlyAllowInstitutionVirtualBackgrounds')?.setValue(isRestricted);

    // Load virtual backgrounds before registering form
    await this.loadVirtualBackgrounds();
    // Set initial value with ResourceItems included (already synced in loadVirtualBackgrounds)
    this.initialFormValue = this.form.value;
    this.integrationsFormService.registerForm(this.form, this.initialFormValue);
  }

  private getAssetValue(assetKey: string, institutionTheme?: InstitutionTheme): string {
    const themeAssetValue =
      institutionTheme?.assets?.[assetKey as keyof typeof institutionTheme.assets];

    if (assetKey === 'squareLogo' && !themeAssetValue) {
      return this.user?.institution?.logo || '';
    }

    return themeAssetValue || '';
  }

  private patchFormWithValues(brandingSettings: Record<string, string | undefined>): void {
    this.form.patchValue({ settings: { ...brandingSettings } });
  }

  uploadLogo(event: Event, logoType: LogoType): void {
    if (event.target instanceof HTMLInputElement) {
      const fileInput = event.target;

      // Check if a file was actually selected
      if (fileInput.files && fileInput.files.length > 0 && fileInput.files[0]) {
        const uploadTask = this.uploadService.uploadToFireStorage(
          fileInput.files[0],
          (data) => {
            this.patchFormWithValues({
              ...this.form.value.settings,
              [logoType]: data,
            });
            this.form.markAsDirty();
            // Reset the file input value to allow selecting the same file again
            fileInput.value = '';
          },
          () => {
            // onFailure callback
            // Reset the file input value on error as well
            fileInput.value = '';
            this.uploadService.showUploadFailedNotification();
          },
          false, // appendFileNameToId
          undefined, // maxFileSizeInMB
          ResourceItemModel.IMAGE, // expectedResourceType
        );

        if (!uploadTask) {
          this.isLoadingVirtualBackgrounds$.next(false);
          fileInput.value = '';
        }
      }
      fileInput.value = '';
    }
  }

  clearAsset(logoType: LogoType): void {
    this.patchFormWithValues({
      ...this.form.value.settings,
      [logoType]: '',
    });
    this.form.markAsDirty();
  }

  resetThemeColorsToDefaults(): void {
    const resetValues: Record<string, string | undefined> = { ...this.form.value.settings };

    if (!this.defaultTheme) {
      return;
    }

    // Reset all color fields to default theme colors (for display)
    // On save, these will be saved as null since they match the default
    this.brandingConfig.sections.forEach((section) => {
      section.fields.forEach((field) => {
        resetValues[field.key] = this.defaultTheme?.colors[field.cssProperty];
      });
    });

    this.patchFormWithValues(resetValues);
    this.form.markAsDirty();
  }

  submitChanges(): void {
    if (!this.user) {
      return;
    }

    // Show confirmation modal before submitting
    this.modalManagerService
      .showConfirmationModal({
        title: this.translateService.instant('Confirm changes?'),
        description: this.translateService.instant(
          "Updating your institution's branding will refresh your current page.",
        ),
      })
      .pipe(untilDestroyed(this))
      .subscribe((confirmed) => {
        if (confirmed) {
          // Use the service to handle form submission
          this.integrationsFormService.dispatchAction(FormAction.SUBMIT);
        }
      });
  }

  private buildUpdatedUserWithBranding(folderId: string | undefined, hasImages: boolean): User {
    const formSettings = this.form.value.settings;
    const currentUser = this.userService.user.getValue()?.user || this.user;

    // Create the updated theme structure using configuration
    const colors: Record<string, string> = { ...currentUser?.institution?.theme?.colors };
    const assets: { horizontalLogo: string; waterMarkLogo: string; squareLogo?: string } = {
      horizontalLogo: '',
      waterMarkLogo: '',
      squareLogo: '',
    };

    // Update colors from configuration
    // Only save colors that differ from the default theme (others will be null/undefined)
    this.brandingConfig.sections.forEach((section) => {
      section.fields.forEach((field) => {
        const formValue = formSettings[field.key];
        const defaultColor = this.defaultTheme?.colors[field.cssProperty] || '';

        if (this.themeService.shouldSaveColor(formValue, defaultColor)) {
          colors[field.cssProperty] = formValue.trim();
        } else {
          // If value matches default or is empty, remove it from the theme so it falls back to default
          delete colors[field.cssProperty];
        }
      });
    });

    this.brandingConfig.assets.forEach((asset) => {
      if (asset.key === 'horizontalLogo') {
        assets.horizontalLogo = formSettings[asset.key] || '';
      } else if (asset.key === 'waterMarkLogo') {
        assets.waterMarkLogo = formSettings[asset.key] || '';
      } else if (asset.key === 'squareLogo') {
        assets.squareLogo = formSettings[asset.key] || '';
      }
    });

    const existingVBSettings = currentUser?.institution?.theme?.virtualBackgroundSettings;
    const virtualBackgroundSettings =
      existingVBSettings || hasImages
        ? {
            ...(existingVBSettings || {}),
            ...(folderId && { folder: folderId }),
            hasImages,
            isRestricted: formSettings.onlyAllowInstitutionVirtualBackgrounds || false,
          }
        : undefined;
    const updatedTheme: InstitutionTheme = {
      colors,
      assets,
      // Set virtualBackgroundSettings only if its been used
      ...(virtualBackgroundSettings && { virtualBackgroundSettings }),
    };

    const shouldClearInstitutionLogo = (formSettings['squareLogo'] ?? '') === '';

    return {
      ...currentUser,
      institution: {
        ...currentUser?.institution,
        logo: shouldClearInstitutionLogo ? '' : currentUser?.institution?.logo,
        theme: updatedTheme,
      },
    } as User;
  }

  private async performSubmit(): Promise<void> {
    const currentUser = this.userService.user.getValue()?.user || this.user;

    try {
      const currentItems = this.getVirtualBackgroundItems();
      const hasImages = currentItems.length > 0;

      // Only get or create folder if there are images to upload
      const folderId = hasImages
        ? await this.vbUtilsService.getOrCreateInstitutionVBsFolder(currentUser?.institution)
        : undefined;

      const persistedUrls = this.virtualBackgrounds.map((bg) => bg.url);

      // Identify items to create (in current but not in persisted)
      const itemsToCreate = currentItems.filter((item) => !persistedUrls.includes(item.url));

      // Identify resources to delete (in persisted but not in current)
      const currentUrls = currentItems.map((item) => item.url);
      const resourcesToDelete = this.virtualBackgrounds.filter(
        (resource) => !currentUrls.includes(resource.url),
      );

      // Create resources for new items
      if (itemsToCreate.length > 0 && folderId) {
        await this.vbUtilsService.createResourcesFromUrls(folderId, itemsToCreate);
      }

      // Delete resources that were removed
      if (resourcesToDelete.length > 0) {
        await this.vbUtilsService.deleteInstitutionVirtualBackgrounds(
          resourcesToDelete.map((r) => r._id),
        );
      }

      const updatedUser = this.buildUpdatedUserWithBranding(folderId, hasImages);

      this.userService
        .updateInstitution(updatedUser)
        .pipe(
          take(1),
          finalize(() => {
            this.integrationsFormService.setLoading(false);
          }),
        )
        .subscribe({
          next: () => {
            window.location.reload();
          },
          error: () => {
            this.settingsNotificationService.showFailureSettingsUpdateNotification();
          },
        });
    } catch {
      this.integrationsFormService.setLoading(false);
      this.settingsNotificationService.showFailureSettingsUpdateNotification();
    }
  }

  cancelChanges(): void {
    this.integrationsFormService.dispatchAction(FormAction.CANCEL);
  }

  loadVirtualBackgrounds(): Promise<void> {
    const hasImages = this.vbUtilsService.hasInstitutionVBsImages(this.user?.institution);
    if (!hasImages) {
      this.virtualBackgrounds = [];
      this.syncVirtualBackgroundsToForm();
      this.isLoadingVirtualBackgrounds$.next(false);
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      this.vbUtilsService.loadInstitutionVirtualBackgrounds(
        this.user?.institution,
        (backgrounds) => {
          // Store full resources (with IDs) for deletion
          this.virtualBackgrounds = [...backgrounds];
          // Sync to form control
          this.syncVirtualBackgroundsToForm();
          this.isLoadingVirtualBackgrounds$.next(false);
          resolve();
        },
        this.isLoadingVirtualBackgrounds$,
      );
    });
  }

  private syncVirtualBackgroundsToForm(): void {
    const settingsGroup = this.form.get('settings') as UntypedFormGroup;
    if (!settingsGroup) {
      return;
    }

    const control = settingsGroup.get('virtualBackgroundUrls');
    if (!control) {
      // Control should exist from initForm, but add it if it doesn't
      settingsGroup.addControl(
        'virtualBackgroundUrls',
        this.formBuilder.control(this.virtualBackgrounds),
      );
    } else {
      // Update the control value with full ResourceItem objects
      control.setValue([...this.virtualBackgrounds] as ResourceItem[], { emitEvent: false });
    }
  }

  private getVirtualBackgroundItems(): ResourceItem[] {
    return (this.form.get('settings.virtualBackgroundUrls')?.value as ResourceItem[]) || [];
  }

  uploadVirtualBackground(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      const fileInput = event.target;
      const file = fileInput.files?.[0];
      if (!file) {
        fileInput.value = '';
        return;
      }

      this.isLoadingVirtualBackgrounds$.next(true);

      // Upload to Firebase only, get URL, add to form control
      const uploadTask = this.uploadService.uploadToFireStorage(
        file,
        (url) => {
          const currentItems = this.getVirtualBackgroundItems();
          const newItem: ResourceItem = {
            _id: '', // Will be set when resource is created on backend
            name: file.name,
            url,
          };
          const settingsGroup = this.form.get('settings') as UntypedFormGroup;
          settingsGroup
            ?.get('virtualBackgroundUrls')
            ?.setValue([newItem, ...currentItems], { emitEvent: true });
          this.form.markAsDirty();
          this.isLoadingVirtualBackgrounds$.next(false);
        },
        () => {
          this.isLoadingVirtualBackgrounds$.next(false);
        },
        false,
        undefined,
        ResourceItemModel.IMAGE,
      );

      if (!uploadTask) {
        this.isLoadingVirtualBackgrounds$.next(false);
        fileInput.value = '';
      }
    }
  }

  deleteVirtualBackground(item: ResourceItem): void {
    // Remove item from form control (don't delete from Firebase)
    const currentItems = this.getVirtualBackgroundItems();
    const settingsGroup = this.form.get('settings') as UntypedFormGroup;
    settingsGroup?.get('virtualBackgroundUrls')?.setValue(
      currentItems.filter((currentItem) => currentItem.url !== item.url),
      { emitEvent: true },
    );
    this.form.markAsDirty();
  }
}
